// Core packages
import { CollectionViewer } from '@angular/cdk/collections';
import { DataSource } from '@angular/cdk/table';

// Third party packages
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';

// Custom packages
import { GroundingsService } from 'src/app/modules/groundings/groundings.service';
import { Grounding } from 'src/app/shared/models/grounding/grounding.model';
import { HelperService } from 'src/app/shared/services/helper.service';

/**
 * @see https://blog.angular-university.io/angular-material-data-table/
 *
 * NB
 * If you need to change this to make a new DataSource
 * just change the following:
 *
 * - Change The class name in the first line
 * - Change DataSource<T> (use your model type) in first line
 * - Change BehaviourSubject<T> in the second line
 * - Change the service inside the class constructor
 * - Change the Observable<T> used in the connect() line
 * - Change the service name inside loadItems()
 */
export class GroundingsDataSource implements DataSource<Grounding> {
  private itemsSubject$ = new BehaviorSubject<Grounding[]>([]);
  private totalCountSubject$ = new BehaviorSubject<number>(0);
  private loadingSubject$ = new BehaviorSubject<boolean>(true);

  public loading$ = this.loadingSubject$.asObservable();
  public totalCount$ = this.totalCountSubject$.asObservable();

  /**
   * Class constructor
   */
  constructor(
    private groundingsService: GroundingsService,
    private helperService: HelperService
  ) { }

  /**
   * Connect data table to itemsSubject$
   *
   * @since 1.0.0
   *
   * @param collectionViewer collection viewer object
   * @returns Observable
   */
  connect(collectionViewer: CollectionViewer): Observable<Grounding[]> {
    return this.itemsSubject$.asObservable();
  }

  /**
   * Disconnect data table from subjects
   *
   * @since 1.0.0
   *
   * @param collectionViewer collection viewer object
   */
  disconnect(collectionViewer: CollectionViewer): void {
    this.itemsSubject$.complete();
    this.totalCountSubject$.complete();
    this.loadingSubject$.complete();
  }

  /**
   * Load items from back-end using service injected in the constructor
   *
   * @since 1.0.0
   *
   * @param [start] Query left offset
   * @param [limit] Results number
   * @param [sort] Sorting field name
   * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
   * @param [search] Filtering objects with filtering data
   */
  loadItems(start?: number, limit?: number, sort?: string, sortVersus?: string, search?: any): void {
    this.loadingSubject$.next(true); // Start loading
    this.groundingsService.getList(start, limit, sort, sortVersus, search)
      .pipe(
        catchError(err => { // If errors, return empty array
          this.helperService.handleError(err);
          return of({ status: true, data: { items: [], totalCount: 0 } });
        }),
        finalize(() => this.loadingSubject$.next(false)) // Stop loading on finalize
      )
      .subscribe((result: any) => {
        this.itemsSubject$.next(result.data.items);
        this.totalCountSubject$.next(result.data.totalCount);
      });
  }
}
